import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SubjectPill } from "@/lib/accents";
import { Loader2, ChevronRight, BookOpen, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Courses() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["student-courses"],
    queryFn: async () => (await api.get("/student/courses")).data,
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const courses = data?.courses ?? [];

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Courses</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Study by topic</h1>
        <p className="mt-1 text-slate-500">Pick a topic for extra practice before a test. This won't disrupt your normal schedule.</p>
      </div>

      {courses.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="no-courses">
          <p className="text-slate-600">No flashcard courses have been assigned to your account yet.</p>
        </div>
      ) : (
        courses.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`courses-course-${c.id}`}>
            <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
              <div>
                <SubjectPill subject={c.subject}>{c.exam_board || c.subject}</SubjectPill>
                <h2 className="mt-2 font-display text-lg font-semibold text-slate-900">{c.name}</h2>
              </div>
              {c.modules.length > 0 && (
                <Button onClick={() => navigate(`/flashcards/study?mode=practice&course=${c.id}`)}
                  data-testid={`practice-course-${c.id}`}
                  className="bg-[#FFC200] text-[#141821] hover:bg-[#141821] hover:text-[#FFC200] rounded-full font-semibold transition-colors">
                  <Layers className="h-4 w-4 mr-2" /> Practice whole course
                </Button>
              )}
            </div>
            {c.modules.length === 0 ? (
              <p className="p-5 text-sm text-slate-400">Flashcards for this course are coming soon.</p>
            ) : (
              <Accordion type="multiple" className="px-2">
                {c.modules.map((m) => (
                  <AccordionItem key={m.id} value={m.id} className="border-slate-100">
                    <AccordionTrigger className="px-3 text-sm font-medium text-slate-800 hover:no-underline">{m.name}</AccordionTrigger>
                    <AccordionContent className="px-3 pb-2">
                      <div className="space-y-1">
                        {m.topics.map((t) => (
                          <div key={t.id} className="rounded-lg">
                            <button data-testid={`practice-topic-${t.id}`}
                              onClick={() => navigate(`/flashcards/study?mode=practice&course=${c.id}&topic=${t.id}`)}
                              className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-left hover:bg-slate-50 transition-colors group">
                              <span className="flex items-center gap-2 text-slate-700 font-medium">
                                <BookOpen className="h-4 w-4 text-slate-400" /> {t.name}
                              </span>
                              <span className="flex items-center gap-2 text-xs text-slate-400">
                                {t.card_count} cards
                                <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                              </span>
                            </button>
                            {t.decks && t.decks.length > 1 && (
                              <div className="pl-9 pr-2 pb-1 space-y-0.5">
                                {t.decks.map((d) => (
                                  <button key={d.id} data-testid={`practice-deck-${d.id}`}
                                    onClick={() => navigate(`/flashcards/study?mode=practice&course=${c.id}&deck=${d.id}`)}
                                    className="w-full flex items-center justify-between rounded-md px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-100 transition-colors">
                                    <span>{d.name}</span>
                                    <span className="text-xs text-slate-400">{d.card_count}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        ))
      )}
    </div>
  );
}
